'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { FileUploadWidget } from './FileUploadWidget';

interface LoadCSVConfigProps {
  nodeId: string;
  config: { fileName?: string; rowCount?: number; separator?: string; encoding?: string; hasHeader?: boolean; tableFormat?: string };
  persistType?: string;
  paramDefaults?: Record<string, unknown>;
}

export function LoadCSVConfig({ nodeId, config, persistType = 'load_csv', paramDefaults }: LoadCSVConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  const [separator, setSeparator] = useState(config.separator || ',');
  const [encoding, setEncoding]   = useState(config.encoding  || 'utf-8');
  const [hasHeader, setHasHeader] = useState(config.hasHeader !== false);

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.separator) setSeparator(config.separator);
    if (config.encoding) setEncoding(config.encoding);
    if (typeof config.hasHeader === 'boolean') setHasHeader(config.hasHeader);
  }, [config.separator, config.encoding, config.hasHeader]);

  const save = (updates: Partial<typeof config>) => {
    updateNodeConfig(nodeId, {
      type: persistType,
      params: { ...paramDefaults, fileName: config.fileName, rowCount: config.rowCount, separator, encoding, hasHeader, ...updates },
    });
  };

  return (
    <div className="space-y-5">
      {/* File upload area */}
      <FileUploadWidget nodeId={nodeId} accept="csv" persistType={persistType} paramDefaults={paramDefaults} />

      {/* Parsing options */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-zinc-500">
          {t('nodes.config.load.parseOptions')}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-[10px] font-medium text-zinc-400">
              {t('nodes.config.load.separator')}
            </label>
            <select value={separator} onChange={(e) => { setSeparator(e.target.value); save({ separator: e.target.value }); }}
              className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            >
              <option value=",">{t('nodes.config.load.comma')}</option>
              <option value="\t">{t('nodes.config.load.tab')}</option>
              <option value=";">{t('nodes.config.load.semicolon')}</option>
              <option value="|">{t('nodes.config.load.pipe')}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-medium text-zinc-400">{t('nodes.config.export.encoding')}</label>
            <select value={encoding} onChange={(e) => { setEncoding(e.target.value); save({ encoding: e.target.value }); }}
              className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            >
              <option value="utf-8">{t('nodes.config.export.utf8')}</option>
              <option value="gbk">{t('nodes.config.export.gbk')}</option>
              <option value="gb2312">GB2312</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" checked={hasHeader}
            onChange={(e) => { setHasHeader(e.target.checked); save({ hasHeader: e.target.checked }); }}
            className="w-4 h-4 text-emerald-600 rounded border-zinc-300 focus:ring-emerald-500"
          />
          <span className="text-sm text-zinc-700">{t('nodes.config.export.includeHeader')}</span>
        </label>
      </div>
    </div>
  );
}
